import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { XploreItemService } from 'src/app/services/xplore-item.service';
import {
  UgcProgramStatus,
  SimpleXploreItemDTO,
  UgcEligibilityResponse,
  XploreItemDTO
} from 'src/app/shared/object/xplorer-be-parsed-classes';
import {UserGeolocationService} from "../../../services/geolocation/user-geolocation.service";
import {TerritoryService} from "../../../services/geolocation/territory.service";
import {getSubsiteSeoContent, SeoSectionContent} from "../../../shared/subsite-seo-content";
import { RouterLink } from '@angular/router';
import { CategoriesComponent } from '../../components/categories/categories.component';
import { FeaturedComponent } from '../../components/featured/featured.component';
import { TerritoryComponent } from '../../components/territory/territory.component';
import { CloseDestinationsComponent } from '../../components/close-destinations/close-destinations.component';

import { HeroComponent } from '../../components/hero/hero.component';
import { MobileHeaderComponent } from '../../components/mobile-header/mobile-header.component';
import { AuthService } from '../../../services/auth/auth.service';
import { UgcService } from '../../../services/ugc.service';
import { EventCacheService } from '../../../services/cache/event-cache.service';

type UgcHomeAction = {
  route: '/ugc/program' | '/ugc/new';
  titleKey: string;
  subtitleKey: string;
  ctaKey: string;
  mode: 'program' | 'create';
};

@Component({
    selector: 'xplorer-bowser-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    imports: [MobileHeaderComponent, HeroComponent, CloseDestinationsComponent, TerritoryComponent, FeaturedComponent, CategoriesComponent, RouterLink, TranslateModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeComponent implements OnInit {
  allXploreItems: SimpleXploreItemDTO[] = [];
  loading = true;
  sectionTitle = '';
  loadingTitle = true;
  readonly skeletons = [1, 2, 3, 4, 5];
  featuredEvent: XploreItemDTO | null = null;
  loadingFeaturedEvent = true;
  seoSections: SeoSectionContent[] = [];
  seoSectionsTitle = '';
  parentDomainLabel = '';
  ugcHomeBackgroundImage = '/assets/login-background.webp';
  readonly parentDomainUrl = 'https://xplorer-platform.com';
  readonly isLoggedIn = signal(false);
  readonly isLoadingUgcEligibility = signal(false);
  readonly ugcEligibility = signal<UgcEligibilityResponse | null>(null);
  readonly ugcHomeAction = computed<UgcHomeAction | null>(() => {
    if (!this.isLoggedIn()) {
      return null;
    }

    const status = this.ugcEligibility()?.programStatus;
    if (!status) {
      return null;
    }

    return this.resolveUgcHomeAction(status);
  });

  private xploreItemService: XploreItemService = inject(XploreItemService);
  private translate: TranslateService = inject(TranslateService);
  private readonly eventCacheService: EventCacheService = inject(EventCacheService);
  private userGeolocationService: UserGeolocationService = inject(UserGeolocationService);
  private territoryService: TerritoryService = inject(TerritoryService);
  private authService: AuthService = inject(AuthService);
  private ugcService: UgcService = inject(UgcService);
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(this.platformId);


  ngOnInit(): void {
    this.ugcHomeBackgroundImage = this.resolveUgcHomeBackgroundImage();
    this.loadTerritorySeoSections();
    this.loadNearbyXplore();
    this.loadUpcomingPopularEvents();
    this.loadUgcEligibility();
  }

  loadNearbyXplore() {
    this.userGeolocationService.getCurrentUserLocation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
      {
        next: (position) => {
          if (!position?.coords) {
            this.loadGenericItems();
            return;
          }
          this.xploreItemService.getNearbyUserSimpleXploreItems(position)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
            next: (data: SimpleXploreItemDTO[]) => {
              this.updateXploreItemsSection(data, 'home.near_you');
            }, error: (error) => {
              console.error('Errore nel recupero dei dati vicini:', error);
              this.loadGenericItems();
            }
          });
        },
        error: (error) => {
          console.error('Errore nel recupero della posizione:', error);
          this.loadGenericItems();
        }
      }
    )
  }

  loadGenericItems() {
    this.xploreItemService.getPagedSimpleXploreItems(0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (data: SimpleXploreItemDTO[]) => {
        this.updateXploreItemsSection(data, 'home.might_like');
      }, error: (error) => {
        console.error('Errore nel recupero degli elementi generici:', error);
        this.loading = false;
        this.loadingTitle = false;
      }
    });
  }

  loadUpcomingPopularEvents(): void {
    this.eventCacheService.getUpcomingPopularEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (data: XploreItemDTO[]) => {
        if (data?.length > 0) {
          this.featuredEvent = data[0];
        }
        this.loadingFeaturedEvent = false;
      }, error: (error) => {
        console.error('Errore nel recupero degli eventi in evidenza:', error);
        this.loadingFeaturedEvent = false;
      }
    });
  }

  updateSectionTitle(title: string) {
    this.sectionTitle = title;
  }

  private updateXploreItemsSection(items: SimpleXploreItemDTO[], titleKey: string): void {
    this.allXploreItems = items;
    this.sectionTitle = this.translate.instant(titleKey);
    this.loading = false;
    this.loadingTitle = false;
  }

  private loadTerritorySeoSections(): void {
    const territoryCode = this.territoryService.getTerritoryCodeFromUrl();
    const selectedLanguage =
      this.translate.currentLang
      || this.getStoredLanguage()
      || this.getBrowserLanguage();
    const seoContent = getSubsiteSeoContent(territoryCode, selectedLanguage);
    this.seoSections = seoContent.content.sections;
    this.seoSectionsTitle = seoContent.content.sectionsTitle;
    this.parentDomainLabel = seoContent.content.parentDomainLabel;
  }

  private getStoredLanguage(): string | null {
    if (!this.isBrowser || typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage.getItem('language');
  }

  private getBrowserLanguage(): string {
    if (!this.isBrowser || typeof navigator === 'undefined') {
      return 'en';
    }
    return navigator.language || 'en';
  }

  private loadUgcEligibility(): void {
    this.authService.isUserLogged()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (isLoggedIn) => {
          this.isLoggedIn.set(isLoggedIn);

          if (!isLoggedIn) {
            this.isLoadingUgcEligibility.set(false);
            this.ugcEligibility.set(null);
            return;
          }

          this.isLoadingUgcEligibility.set(true);
          this.ugcService.getEligibility()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (eligibility) => {
                this.ugcEligibility.set(eligibility);
                this.isLoadingUgcEligibility.set(false);
              },
              error: () => {
                this.ugcEligibility.set(null);
                this.isLoadingUgcEligibility.set(false);
              }
            });
        },
        error: () => {
          this.isLoggedIn.set(false);
          this.ugcEligibility.set(null);
          this.isLoadingUgcEligibility.set(false);
        }
      });
  }

  private resolveUgcHomeAction(status: UgcProgramStatus): UgcHomeAction | null {
    switch (status) {
      case 'INVITED':
        return {
          route: '/ugc/program',
          titleKey: 'ugc_home.invited.title',
          subtitleKey: 'ugc_home.invited.subtitle',
          ctaKey: 'ugc_home.invited.cta',
          mode: 'program'
        };
      case 'ELIGIBLE':
        return {
          route: '/ugc/program',
          titleKey: 'ugc_home.eligible.title',
          subtitleKey: 'ugc_home.eligible.subtitle',
          ctaKey: 'ugc_home.eligible.cta',
          mode: 'program'
        };
      case 'ACCEPTED':
        return {
          route: '/ugc/new',
          titleKey: 'ugc_home.accepted.title',
          subtitleKey: 'ugc_home.accepted.subtitle',
          ctaKey: 'ugc_home.accepted.cta',
          mode: 'create'
        };
      default:
        return null;
    }
  }

  private resolveUgcHomeBackgroundImage(): string {
    return '/assets/login-background.webp';
  }
}
